import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { surveyAPI } from '../services/api';
import type { Survey } from '../types';

const AllCompletedSurveys: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isManager = user?.role === 'manager' || user?.role === 'admin';

  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        const res = await surveyAPI.getSurveys();
        const list: Survey[] = res.data || [];
        const nonTemplates = list.filter((s: any) => !(s as any).isTemplate);
        const now = new Date();
        const completed = nonTemplates.filter((s: any) => {
          const ended = !(s as any).isActive || new Date((s as any).endDate) < now;
          const comp = (s as any).completion || {};
          const reachedTarget = comp && comp.target > 0 && comp.responses >= comp.target;
          return ended || reachedTarget;
        });
        // Sort newest completed first
        completed.sort(
          (a: any, b: any) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
        );
        setSurveys(completed);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to fetch surveys');
      } finally {
        setLoading(false);
      }
    };

    fetchSurveys();
  }, []);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/surveys')}
          sx={{ alignSelf: 'flex-start' }}
        >
          Back to Surveys
        </Button>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              All Completed Surveys
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Review all surveys that have finished or reached their response target.
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {surveys.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            px: 2,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No completed surveys
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Once a survey has ended or reached its target, it will appear here.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {surveys.map((survey: any) => (
            <Grid item xs={12} sm={6} md={4} key={survey._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  boxShadow: 1,
                  transition: 'transform 0.15s ease-out, box-shadow 0.15s ease-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {survey.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {survey.description || 'No description provided'}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                    {formatDate(survey.startDate)} – {formatDate(survey.endDate)}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  {isManager && (
                    <Button
                      size="small"
                      variant="contained"
                      fullWidth
                      onClick={() => navigate(`/surveys/${survey._id}/responses`)}
                    >
                      View Results
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default AllCompletedSurveys;
